import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Alert,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '@/services/api';
import { ReleaseType } from '@/types';

export default function ReleaseTypes() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<ReleaseType | null>(null);
  const [typeToDelete, setTypeToDelete] = useState<ReleaseType | null>(null);
  
  const [name, setName] = useState('');
  const [descriptionTemplate, setDescriptionTemplate] = useState('');
  const [requiresVersion, setRequiresVersion] = useState(false);
  const [versionUrl, setVersionUrl] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [displayOrder, setDisplayOrder] = useState(0);

  const { data: releaseTypes } = useQuery({
    queryKey: ['releaseTypes'],
    queryFn: () => api.getReleaseTypes(),
  });

  const createMutation = useMutation({
    mutationFn: (releaseType: Partial<ReleaseType>) => api.createReleaseType(releaseType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releaseTypes'] });
      handleCloseDialog();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (releaseType: ReleaseType) => api.updateReleaseType(releaseType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releaseTypes'] });
      handleCloseDialog();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteReleaseType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['releaseTypes'] });
      setDeleteDialogOpen(false);
      setTypeToDelete(null);
    },
  });

  const handleOpenDialog = (releaseType?: ReleaseType) => {
    if (releaseType) {
      setEditingType(releaseType);
      setName(releaseType.name);
      setDescriptionTemplate(releaseType.descriptionTemplate);
      setRequiresVersion(releaseType.requiresVersion);
      setVersionUrl(releaseType.versionUrl ?? '');
      setIsDefault(releaseType.isDefault);
      setDisplayOrder(releaseType.displayOrder);
    } else {
      setEditingType(null);
      setName('');
      setDescriptionTemplate('');
      setRequiresVersion(false);
      setVersionUrl('');
      setIsDefault(false);
      setDisplayOrder((Array.isArray(releaseTypes) ? releaseTypes.length : 0) + 1);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingType(null);
    setName('');
    setDescriptionTemplate('');
    setRequiresVersion(false);
    setVersionUrl('');
    setIsDefault(false);
    setDisplayOrder(0);
  };

  const handleSave = () => {
    const typeData = { name, descriptionTemplate, requiresVersion, versionUrl: versionUrl || undefined, isDefault, displayOrder };
    if (editingType) {
      updateMutation.mutate({ ...editingType, ...typeData });
    } else {
      createMutation.mutate(typeData);
    }
  };

  const handleDeleteClick = (releaseType: ReleaseType) => {
    setTypeToDelete(releaseType);
    setDeleteDialogOpen(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Release Types
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Release Type
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Release types define templates for different kinds of releases (e.g., Upshop 360, Mobile, Cloud Connector).
        Available placeholders: <code>{"{{ReleaseDate}}"}</code>, <code>{"{{TodaysDate}}"}</code>, <code>{"{{Version}}"}</code>, <code>{"{{VersionUrl}}"}</code>, <code>{"{{ProductCount}}"}</code>, <code>{"{{ItemCount}}"}</code>
      </Alert>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Template Preview</TableCell>
              <TableCell>Version Required</TableCell>
              <TableCell>Default</TableCell>
              <TableCell>Order</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(Array.isArray(releaseTypes) ? releaseTypes : []).sort((a, b) => a.displayOrder - b.displayOrder).map((releaseType) => (
              <TableRow key={releaseType.id}>
                <TableCell>{releaseType.name}</TableCell>
                <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {releaseType.descriptionTemplate}
                  </Typography>
                </TableCell>
                <TableCell>
                  {releaseType.requiresVersion ? (
                    <Chip label="Yes" size="small" color="primary" />
                  ) : (
                    <Chip label="No" size="small" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>
                  {releaseType.isDefault ? (
                    <Chip label="Default" size="small" color="success" />
                  ) : '-'}
                </TableCell>
                <TableCell>{releaseType.displayOrder}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleOpenDialog(releaseType)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => handleDeleteClick(releaseType)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {(!Array.isArray(releaseTypes) || releaseTypes.length === 0) && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No release types configured</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingType ? 'Edit Release Type' : 'Add Release Type'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Description Template"
              value={descriptionTemplate}
              onChange={(e) => setDescriptionTemplate(e.target.value)}
              multiline
              rows={4}
              fullWidth
              helperText="Use placeholders: {{ReleaseDate}}, {{TodaysDate}}, {{Version}}, {{VersionUrl}}, {{ProductCount}}, {{ItemCount}}"
            />
            <TextField
              label="Display Order"
              type="number"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
              sx={{ width: 150 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={requiresVersion}
                  onChange={(e) => setRequiresVersion(e.target.checked)}
                />
              }
              label="Requires version number"
            />
            {requiresVersion && (
              <TextField
                label="Version URL (default)"
                value={versionUrl}
                onChange={(e) => setVersionUrl(e.target.value)}
                fullWidth
                placeholder="https://..."
                helperText="Default URL for {{VersionUrl}} when publishing. Used in the description template."
              />
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                />
              }
              label="Set as default"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={createMutation.isPending || updateMutation.isPending || !name}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete release type <strong>{typeToDelete?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => typeToDelete && deleteMutation.mutate(typeToDelete.id)}
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
